/**
 * Error Handler Utilities
 * 
 * Centralized error handling for consistent error management
 */

export interface AppError {
  message: string
  code?: string
  details?: any
  timestamp: Date
}

export const createError = (message: string, code?: string, details?: any): AppError => ({
  message,
  code,
  details,
  timestamp: new Date()
})

export const handleApiError = (error: unknown): AppError => {
  if (error instanceof Error) {
    return createError(error.message)
  }
  
  if (typeof error === 'string') {
    return createError(error)
  }
  
  return createError('An unexpected error occurred')
}

export const isNetworkError = (error: AppError): boolean => {
  return error.message.includes('fetch') || 
         error.message.includes('network') || 
         error.message.includes('connection')
}

export const isAuthError = (error: AppError): boolean => {
  return error.message.includes('unauthorized') || 
         error.message.includes('authentication') ||
         error.message.includes('token')
}

export const isValidationError = (error: AppError): boolean => {
  return error.message.includes('validation') || 
         error.message.includes('invalid') ||
         error.message.includes('required')
}

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message
  }
  
  if (typeof error === 'string') {
    return error
  }
  
  return 'An unexpected error occurred'
}

export const logError = (error: AppError, context?: string): void => {
  console.error(`[${context || 'App'}] Error:`, {
    message: error.message,
    code: error.code,
    timestamp: error.timestamp,
    details: error.details
  })
}

// Error types for different scenarios
export const ERROR_TYPES = {
  NETWORK: 'NETWORK_ERROR',
  AUTH: 'AUTH_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  SERVER: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR'
} as const 